import { isValidUUID } from './validators';
import { HEROKU_BACKEND_URL } from '../config';

// Backend Flask API base path (via nginx proxy)
const FLASK_API_BASE = HEROKU_BACKEND_URL || '/api/backend';

function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => 
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing JWT:', e);
    return null;
  }
}

const getUserInfo = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  const decoded = parseJwt(token);
  if (!decoded) return null;

  return {
    username: decoded.username,
    role: decoded.role,
    plan: decoded.plan,
    user_id: decoded.user_id
  };
};

const authenticatedFetch = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const pat = localStorage.getItem('pat');
  
  if (!token && !pat) {
    throw new Error('No authentication token found');
  }

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${pat || token}`
    },
  };

  try {
    const response = await fetch(`${window.location.origin}/api${endpoint}`, {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    });

    if (response.status === 403) {
      const data = await response.json();
      if (data.expired) {
        localStorage.removeItem('token');
        localStorage.removeItem('pat');
        window.location.href = '/login';
        throw new Error('Token expired');
      }
      throw new Error(data.message || 'Forbidden');
    }
  
      const contentType = response.headers.get('content-type');
      if (!response.ok) {
        const errorData = await response.json();
        
        if (response.status === 400 && errorData.errors) {
          const error = new Error('Validation error');
          error.errors = errorData.errors;
          throw error;
        }
        
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }
  
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return await response.text();
    } catch (error) {
      console.error('Error during authenticated fetch:', error);
      throw error;
    }
  };

  const verifyPAT = async (pat) => {
    try {
      const response = await fetch('/api/verify-pat', {
        headers: {
          'Authorization': `Bearer ${pat}`
        }
      });
  
      if (!response.ok) {
        throw new Error('Invalid PAT');
      }
  
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error verifying PAT:', error);
      throw error;
    }
  };

  const unauthenticatedFetch = async (endpoint, options = {}) => {
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
  
    try {
      const response = await fetch(`/api${endpoint}`, {
        ...defaultOptions,
        ...options,
        headers: {
          ...defaultOptions.headers,
          ...options.headers,
        },
      });
  
      const data = await response.json();
  
      if (response.status === 403 && data.requires2FA) {
        return data;
      }
  
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
  
      return data;
    } catch (error) {
      console.error('Error during fetch:', error);
      throw error;
    }
  };

  const addRunner = async (runnerData) => {
    try {
      const { team_id, ...data } = runnerData;
      const contextParam = team_id ? `team_id=${team_id}` : 'personal=true';
      
      const response = await authenticatedFetch(`/runners?${contextParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.error) {
        throw new Error(response.message || 'Error creating runner');
      }

      return response;
    } catch (error) {
      console.error('Error in addRunner:', error);
      throw error;
    }
  };

  const updateRunner = async (runnerId, runnerData) => {
    try {
      const { team_id, ...data } = runnerData;
      const contextParam = team_id ? `team_id=${team_id}` : 'personal=true';
      
      const response = await authenticatedFetch(`/runners/${runnerId}?${contextParam}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.error) {
        throw new Error(response.message || 'Error updating runner');
      }

      return response;
    } catch (error) {
      console.error('Error in updateRunner:', error);
      throw error;
    }
  };

  const deleteRunner = async (runnerId, isPersonal = false, teamId = null) => {
    try {
      const contextParam = isPersonal ? 'personal=true' : `team_id=${teamId}`;
      
      const response = await authenticatedFetch(`/runners/${runnerId}?${contextParam}`, {
        method: 'DELETE',
      });

      if (response.error) {
        throw new Error(response.message || 'Error deleting runner');
      }

      return response;
    } catch (error) {
      console.error('Error in deleteRunner:', error);
      throw error;
    }
  };

  const authenticatedHerokuFetch = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found');
    }
  
    const defaultOptions = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    };
  
    try {
      // Remove /api prefix from endpoint since FLASK_API_BASE already points to /api/backend
      const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
      const response = await fetch(`${FLASK_API_BASE}${cleanEndpoint}`, {
        ...defaultOptions,
        ...options,
        headers: {
          ...defaultOptions.headers,
          ...options.headers,
        },
      });

    if (response.status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw new Error('Token expired');
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error during Heroku fetch:', error);
    throw error;
  }
};

const startRunner = async (runner_id, execution_id) => {
  try {
    if (!execution_id) {
      // 1. Créer une nouvelle exécution si pas d'execution_id fourni
      const execution = await authenticatedFetch('/runner-executions', {
        method: 'POST',
        body: JSON.stringify({ runner_id })
      });

      if (!execution?.execution_id) {
        throw new Error('Failed to create execution');
      }
      execution_id = execution.execution_id;
    }

    // 2. Démarrer sur Heroku avec l'execution_id
    const herokuResponse = await authenticatedHerokuFetch('/api/start_runner', {
      method: 'POST',
      body: JSON.stringify({
        execution_id: execution_id
      })
    });

    // 3. Mettre à jour le statut de l'exécution
    await authenticatedFetch(`/runner-executions/${execution_id}`, {
      method: 'PUT',
      body: JSON.stringify({ 
        status: 'running',
        heroku_runner_id: herokuResponse.heroku_runner_id 
      })
    });

    return { execution_id, heroku_runner_id: herokuResponse.heroku_runner_id };
  } catch (error) {
    console.error('Error starting runner:', error);
    throw error;
  }
};

const updateRunnerStatus = async (runner_id, status, lastrun_at = null, finished_at = null, results_count = 0) => {
  try {
    if (!runner_id || !isValidUUID(runner_id)) {
      console.error('Invalid or missing runner_id:', runner_id);
      return null;
    }

    const response = await authenticatedFetch(`/update-runner/${runner_id}`, {
      method: 'PUT',
      body: JSON.stringify({
        status,
        lastrun_at,
        finished_at,
        results_count
      })
    });

    return response;
  } catch (error) {
    console.error('Error updating runner status:', error);
    throw error;
  }
};

const stopRunner = async (runner_id) => {
  try {
    // 1. Récupérer l'exécution active
    const execution = await authenticatedFetch(
      `/runner-executions/active/${runner_id}`
    );

    if (!execution?.heroku_runner_id) {
      throw new Error('No active execution found');
    }

    // 2. Arrêter le processus sur Heroku
    await authenticatedHerokuFetch(
      `/api/stop_runner/${execution.heroku_runner_id}`,
      { method: 'POST' }
    );

    // 3. Mettre à jour le statut de l'exécution
    await authenticatedFetch(
      `/runner-executions/${execution.execution_id}`,
      {
        method: 'PUT',
        body: JSON.stringify({ 
          status: 'stopped',
          force_stop: true 
        })
      }
    );

    return { success: true };
  } catch (error) {
    console.error('Error stopping runner:', error);
    throw error;
  }
};

const fetchRunnerResults = async (runner_id, execution_id = null) => {
  try {
    const params = new URLSearchParams({ limit: '0' });
    if (execution_id) params.set('execution_id', execution_id);
    const endpoint = `/results/${runner_id}?${params.toString()}`;
    const response = await authenticatedFetch(endpoint);

    if (response.runner) {
      response.runner.total_executions = response.executions?.length || 0;
    }
    
    return response;
  } catch (error) {
    console.error('Error fetching runner results:', error);
    throw error;
  }
};

const deleteExecution = async (executionId) => {
  const response = await authenticatedFetch(`/delete-execution/${executionId}`, {
    method: 'DELETE'
  });
  return response;
};

const syncRunnerStatus = async (runner_id) => {
  try {
    // Récupérer directement le statut actuel depuis la base de données
    const response = await authenticatedFetch(`/runners/${runner_id}`);
    if (!response) return null;

    return response.current_status || 'stopped';
  } catch (error) {
    console.error('Error syncing runner status:', error);
    return null;
  }
};

const bulkDeleteExecutions = async (runnerId) => {
  try {
    const response = await authenticatedFetch(`/delete-executions/bulk/${runnerId}`, {
      method: 'DELETE'
    });
    
    if (!response || !response.deleted_count) {
      return {
        deleted_count: 0,
        message: response?.message || 'No executions were deleted'
      };
    }
    
    return response;
  } catch (error) {
    console.error('Error during bulk deletion:', error);
    throw error;
  }
};

const verifyPassword = async (password) => {
  try {
    const response = await authenticatedFetch('/verify-password', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });

    if (!response.signature) {
      throw new Error('Invalid server response');
    }

    return response;
  } catch (error) {
    console.error('Error verifying password:', error);
    throw error;
  }
};

const verifyAndSaveConfiguration = async (password, configData) => {
  try {
    // 1. Vérifier le mot de passe et obtenir un token de validation
    const verificationResponse = await verifyPassword(password);
    if (!verificationResponse.valid || !verificationResponse.signature) {
      throw new Error('Invalid password');
    }

    // 2. Envoyer la configuration webhooks avec le token de validation
    const response = await authenticatedFetch('/webhooks', {
      method: 'POST',
      body: JSON.stringify({
        ...configData,
        verificationSignature: verificationResponse.signature
      })
    });

    return response;
  } catch (error) {
    console.error('Error in verifyAndSaveConfiguration:', error);
    throw error;
  }
};

const updateFinding = async (findingId, updates) => {
  try {
    const response = await authenticatedFetch(`/findings/${findingId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response;
  } catch (error) {
    console.error('Error updating finding:', error);
    throw error;
  }
};

const bulkUpdateFindings = async (resultIds, updates) => {
  try {
    const response = await authenticatedFetch('/findings/bulk-update', {
      method: 'POST',
      body: JSON.stringify({
        result_ids: resultIds,
        ...updates
      })
    });
    return response;
  } catch (error) {
    console.error('Error bulk updating findings:', error);
    throw error;
  }
};

const toggleFindingFlag = async (findingId, flagged) => {
  try {
    const response = await authenticatedFetch(`/findings/${findingId}/flag`, {
      method: flagged ? 'POST' : 'DELETE'
    });
    return response;
  } catch (error) {
    console.error('Error toggling finding flag:', error);
    throw error;
  }
};

/**
 * Trigger deep scan verification for a specific result/token
 * @param {string} resultId - UUID of the result to verify
 * @returns {Promise<Object>} - Verification status and task info
 */
const verifyToken = async (resultId) => {
  try {
    const response = await authenticatedFetch(`/results/${resultId}/verify`, {
      method: 'POST'
    });
    return response;
  } catch (error) {
    console.error('Error verifying token:', error);
    throw error;
  }
};

/**
 * Generate a curl verification command for a detected secret via LLM
 * @param {string} resultId - UUID of the result
 * @returns {Promise<Object>} - { curl_command, expected_success, expected_failure, warning }
 */
const generateCurlCommand = async (resultId) => {
  try {
    const response = await authenticatedFetch('/llm/generate-curl', {
      method: 'POST',
      body: JSON.stringify({ result_id: resultId })
    });
    return response;
  } catch (error) {
    console.error('Error generating curl command:', error);
    throw error;
  }
};

export {
  parseJwt,
  authenticatedFetch,
  unauthenticatedFetch,
  getUserInfo,
  addRunner,
  updateRunner,
  deleteRunner,
  startRunner,
  stopRunner,
  updateRunnerStatus,
  fetchRunnerResults,
  deleteExecution,
  syncRunnerStatus,
  bulkDeleteExecutions,
  verifyPassword,
  verifyAndSaveConfiguration,
  updateFinding,
  bulkUpdateFindings,
  toggleFindingFlag,
  verifyPAT,
  verifyToken,
  generateCurlCommand
};